import { createAsyncThunk, createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { restApi } from '@icp/settings';
import { pick } from 'lodash-es';
import { selectPbcToken, selectProjectToken } from './contextSlice';
import { selectFormEntity } from './formEntitySlice';

const Convertor = {
  extension: {
    toDTO: (obj) => {
      return obj;
    },
    fromDTO: (obj) => {
      return obj;
    },
  },
};

const selectThis = (state) => state.extension;
const selectContext = createSelector(selectThis, (state) => state.context);
export const selectExtensionListFetching = createSelector(
  selectThis,
  (state) => state.extensionListFetching,
);
export const selectExtensionList = createSelector(selectThis, (state) => state.extensionList);

export const fetchExtensionList = createAsyncThunk(
  'extension/fetchExtensionList',
  async (payload, { signal, getState }) => {
    const { level } = payload;
    const { device, searchText } = selectContext(getState());
    const projectToken = selectProjectToken(getState());
    const levelCode = {
      project: 0,
      partner: 1,
      global: 2,
    }[level];
    const response = await restApi.get('/form/api/ui-plugin-component/list', {
      signal,
      params: {
        device: device || undefined,
        searchText: searchText || undefined,
        projectToken,
        level: levelCode,
      },
    });
    return response.map((x) => Convertor.extension.fromDTO(x));
  },
);

export const editExtension = createAsyncThunk(
  'extension/editExtension' /*  */,
  async (payload, { signal }) => {
    const { data } = payload;
    const response = await restApi.put(
      `/form/api/ui-plugin-component`,
      Convertor.extension.toDTO(data),
      { signal },
    );
    return Convertor.extension.fromDTO(response);
  },
);

export const deleteExtension = createAsyncThunk(
  'extension/deleteExtension',
  async (payload, { signal }) => {
    const { data } = payload;
    await restApi.delete(`/form/api/ui-plugin-component/${data.id}`, { signal });
  },
);

export const approveExtension = createAsyncThunk(
  'extension/approveExtension',
  async (payload, { signal }) => {
    const { data } = payload;
    const response = await restApi.put(
      `/form/api/ui-plugin-component/approve/${data.id}`,
      null /*                      */,
      { signal },
    );
    return Convertor.extension.fromDTO(response);
  },
);

export const promoteExtension = createAsyncThunk(
  'extension/promoteExtension',
  async (payload, { signal }) => {
    const { data } = payload;
    await restApi.put(
      `/form/api/ui-plugin-component/improve/${data.id}`,
      null /*                      */,
      { signal },
    );
  },
);

export const saveToComponentLibrary = createAsyncThunk(
  'extension/saveToComponentLibrary',
  async (payload, { getState }) => {
    const projectToken = selectProjectToken(getState());
    const pbcToken = selectPbcToken(getState());
    const formEntityToken = selectFormEntity(getState())?.token;

    const { fieldInfo, fieldMeta } = payload;

    const field = JSON.stringify(
      pick(fieldInfo, [
        'component',
        'componentProps.extensionType',
        'componentProps.suppressShadow',
        'componentProps.extensionJs',
        'componentProps.extensionCss',
        'fields',
      ]),
    );
    const response = await restApi.post(
      `/form/api/ui-plugin-component`,
      Convertor.extension.toDTO({
        projectToken,
        pbcToken,
        formEntityToken,
        field,
        jsName: fieldInfo.componentProps?.extensionJs?.filename,
        cssName: fieldInfo.componentProps?.extensionCss?.filename,
        ...fieldMeta,
      }),
    );
    return Convertor.extension.fromDTO(response);
  },
);

const slice = createSlice({
  name: 'extension',
  initialState: {
    context: {
      device: null,
      searchText: null,
    },
    extensionListFetching: {
      project: false,
      partner: false,
      global: false,
    },
    extensionList: {
      project: [],
      partner: [],
      global: [],
    },
  },
  reducers: {
    setExtensionContext: (state, action) => {
      state.context = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtensionList.pending, (state, action) => {
        state.extensionListFetching[action.meta.arg.level] = true;
      })
      .addCase(fetchExtensionList.rejected, (state, action) => {
        state.extensionListFetching[action.meta.arg.level] = false;
      })
      .addCase(fetchExtensionList.fulfilled, (state, action) => {
        state.extensionListFetching[action.meta.arg.level] = false;
        state.extensionList[action.meta.arg.level] = action.payload;
      })
      .addCase(deleteExtension.fulfilled, (state, action) => {
        state.extensionList[action.meta.arg.level] = state.extensionList[action.meta.arg.level]
          //
          .filter((item) => item.id !== action.meta.arg.data.id);
      })
      .addMatcher(
        isAnyOf(
          editExtension.fulfilled,
          approveExtension.fulfilled,
          //
        ),
        (state, action) => {
          state.extensionList[action.meta.arg.level] = state.extensionList[action.meta.arg.level]
            //
            .map((item) => (item.id === action.payload.id ? action.payload : item));
        },
      );
  },
});

export const { setExtensionContext } = slice.actions;

export default slice.reducer;
